import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../services/api';
import type { Project } from '../../../types/project';

// Import the Community type
import type { Community } from '../../../types/community';

interface ProjectFormProps {
  initialData?: Project;
  onSubmit: (data: Project) => Promise<void>;
  isSubmitting: boolean;
  error: string | null;
  onCancel: () => void;
  submitLabel: string;
}

export default function ProjectForm({
  initialData,
  onSubmit,
  isSubmitting,
  error,
  onCancel,
  submitLabel
}: ProjectFormProps) {
  // Fetch communities with simpler configuration
  const { 
    data: communities, 
    isLoading: isLoadingCommunities, 
    error: communitiesError 
  } = useQuery<Community[]>({
    queryKey: ['communities'],
    queryFn: api.communities.getAll
  });

  // Debug logging
  React.useEffect(() => {
    if (communitiesError) {
      console.error('Error fetching communities:', communitiesError);
    }
    
    if (communities) {
      console.log('Communities data loaded:', communities);
      console.log('Number of communities:', communities.length);
      
      if (communities.length > 0) {
        console.log('First community:', communities[0]);
        console.log('Community properties:', Object.keys(communities[0]));
      }
    }
  }, [communities, communitiesError]);

  const [formData, setFormData] = React.useState<Project>({
    proj_id: initialData?.proj_id || '',
    proj_comm: initialData?.proj_comm || '',
    proj_desc: initialData?.proj_desc || '',
    proj_details: initialData?.proj_details || ''
  });
  
  // Track character counts
  const descCharCount = formData.proj_desc.length;
  const detailsCharCount = formData.proj_details.length;
  
  // Character limits based on database schema
  const DESC_CHAR_LIMIT = 60;
  const DETAILS_CHAR_LIMIT = 255;
  
  // Local form validation error state
  const [validationError, setValidationError] = React.useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous validation errors
    setValidationError(null);
    
    // Check if communities are loaded
    if (isLoadingCommunities) {
      setValidationError('Please wait for communities to load');
      return;
    }
    
    // Check if communities data is available
    if (!communities || communities.length === 0) {
      setValidationError('No communities available. Please try again later.');
      return;
    }
    
    // Validate form data
    if (!formData.proj_comm) {
      console.error('Community is required');
      setValidationError('Please select a community');
      return;
    }
    
    // Log the form data being submitted
    console.log('Submitting project form data:', formData);
    
    try {
      await onSubmit(formData);
    } catch (err) {
      console.error('Error submitting project form:', err);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
      {/* Display API error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      
      {/* Display form validation error */}
      {validationError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {validationError}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Project ID *
          </label>
          <input
            type="text"
            value={formData.proj_id}
            onChange={(e) => setFormData(prev => ({ ...prev, proj_id: e.target.value }))}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
            required
          />
          <p className="mt-1 text-sm text-gray-500">
            Enter the project identifier (e.g., "ARIKKI23-ANGU", "BARGE-LANDING24-MILY")
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Community *
          </label>
          {/* Display loading indicator if communities are loading */}
          {isLoadingCommunities && (
            <div className="mt-1 text-sm text-gray-500">Loading communities...</div>
          )}
          
          {/* Display error message if there's an error */}
          {communitiesError && (
            <div className="mt-1 text-sm text-red-500">
              Error loading communities: {communitiesError.message}
            </div>
          )}
          
          {/* Show number of communities loaded for debugging */}
          {communities && communities.length > 0 && (
            <div className="mt-1 mb-1 text-xs text-gray-400">
              {communities.length} communities available
            </div>
          )}
          
          <div className="relative">
            <select
              value={formData.proj_comm}
              onChange={(e) => setFormData(prev => ({ ...prev, proj_comm: e.target.value }))}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary appearance-none bg-white cursor-pointer hover:border-primary"
              required
              disabled={isLoadingCommunities}
              style={{ 
                backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")",
                backgroundPosition: "right 0.5rem center",
                backgroundRepeat: "no-repeat",
                backgroundSize: "1.5em 1.5em",
                paddingRight: "2.5rem"
              }}
            >
            <option value="">Please select a Community</option>
            {communities?.map((community) => (
              <option 
                key={community.comm_id} 
                value={community.comm_id}
                className="py-1"
              >
                {community.comm_name} ({community.comm_id})
              </option>
            ))}
            </select>
            {/* Add a visual indicator for the dropdown */}
            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Select the community for this project
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Description *
          </label>
          <input
            type="text"
            value={formData.proj_desc}
            onChange={(e) => setFormData(prev => ({ ...prev, proj_desc: e.target.value }))}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
            maxLength={DESC_CHAR_LIMIT}
            required
          />
          <div className="flex justify-between">
            <p className="mt-1 text-sm text-gray-500">
              Enter a brief description of the project (max {DESC_CHAR_LIMIT} characters)
            </p>
            <p className={`mt-1 text-sm ${descCharCount >= DESC_CHAR_LIMIT * 0.9 ? 'text-amber-600' : 'text-gray-500'}`}>
              {descCharCount}/{DESC_CHAR_LIMIT}
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Details
          </label>
          <textarea
            value={formData.proj_details}
            onChange={(e) => setFormData(prev => ({ ...prev, proj_details: e.target.value }))}
            rows={4}
            maxLength={DETAILS_CHAR_LIMIT}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
          />
          <div className="flex justify-between">
            <div className="mt-1">
              <p className="text-sm text-gray-500">
                Enter detailed information about the project (optional, max {DETAILS_CHAR_LIMIT} characters)
              </p>
              <p className="text-sm text-gray-500 italic">
                Add URL to the document repository as needed
              </p>
            </div>
            <p className={`mt-1 text-sm ${detailsCharCount >= DETAILS_CHAR_LIMIT * 0.9 ? 'text-amber-600' : 'text-gray-500'}`}>
              {detailsCharCount}/{DETAILS_CHAR_LIMIT}
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-dark disabled:opacity-50"
        >
          {isSubmitting ? 'Submitting...' : submitLabel}
        </button>
      </div>
    </form>
  );
}
